import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Presale -> IPO instrument conversion.
 *
 * A securities position is a Balance row whose currency IS the instrument's
 * `instrumentInternalId` (`${tickerId}~${sourceId}`), so a presale bond held as
 * `AM0001~OTC` becomes a DIFFERENT instrument when it lists as `AM0001~AMX`.
 * This run moves every holder across 1:1, cancels the open orders on the old
 * code and flips the instrument flags.
 *
 * Two things about it drive the whole UI:
 *  - There is no ratio. One old unit becomes exactly one new unit.
 *  - There is no revert. Cancelled orders are gone and no compensating run
 *    exists, which is why the preview is the safety mechanism and why the
 *    execute path refuses to send anything the preview did not authorise.
 */

/** Terminal statuses plus `running`, which also covers a crashed run. */
export type ConversionStatus =
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type ConversionEntryStatus = 'moved' | 'skipped' | 'failed';

/**
 * Pre-flight failures. Always branch on the code — the messages are prose and
 * are not part of the contract.
 */
export type ConversionBlockerCode =
  | 'NOT_PRESALE'
  | 'FROM_NOT_ACTIVE'
  | 'RUN_IN_PROGRESS'
  | 'PENDING_SECURITIES_WITHDRAW'
  | 'PENDING_WITHDRAW'
  | 'OTC_FINALIZED_ORDERS'
  | 'TOO_MANY_HOLDERS'
  | 'AMBIGUOUS_INSTRUMENT'
  | 'ISIN_MISMATCH'
  | 'PRICE_CURRENCY_MISMATCH'
  | 'NO_PRICE_FOR_TARGET'
  /** Warning only — never a blocker. See BLOCKER_META for why it matters. */
  | 'MISSING_ISIN';

export interface ConversionBlocker {
  code: ConversionBlockerCode;
  message?: string;
}

/**
 * The four flags that downgrade a blocker to a warning. None should be used
 * routinely — see BLOCKER_META for what each one actually risks.
 */
export interface ConversionOverrides {
  /**
   * Converts an ordinary, non-presale source. This is how re-coding a live
   * instrument — one whose `tickerId` was created wrong, with the ISIN in it —
   * is authorised.
   */
  allowNonPresale?: boolean;
  allowIsinMismatch?: boolean;
  allowPriceCurrencyMismatch?: boolean;
  allowNoPrice?: boolean;
}

export interface ConversionRequest extends ConversionOverrides {
  /** Source `instrumentInternalId`, e.g. `AM0001~OTC`. */
  from: string;
  /** Target `instrumentInternalId`, e.g. `AM0001~AMX`. */
  to: string;
}

export interface RunConversionPayload extends ConversionRequest {
  /**
   * Required by the backend. Without it the call is refused with
   * ERROR_VALIDATION and nothing happens, so a replayed or double-submitted
   * request cannot move a portfolio by accident.
   */
  confirm: true;
  note?: string;
}

/** The instrument summaries the preview returns for each side. */
export interface ConversionInstrument {
  instrumentInternalId: string;
  isin?: string;
  status?: string;
  isPresale?: boolean;
  /**
   * Target only: the `isPresale` the run will leave the target with. It is
   * derived from the target row, never requested — whatever it was created
   * with is what it ends up with.
   */
  isPresaleAfter?: boolean;
  presalePrice?: number | string | null;
  priceCurrencyId?: string;
  /** Decimal string or number depending on the field — render, do not compute. */
  bondParValue?: number | string;
  lastPrice?: string;
}

export interface ConversionHolders {
  count: number;
  /** Decimal string. */
  totalQuantity: string;
  negative: number;
}

export interface ConversionOpenOrders {
  stock?: { symbols?: string[]; count?: number };
  otc?: { count?: number; orderIDs?: string[] };
}

export interface ConversionSampleHolder {
  userId: string;
  /** Decimal string. */
  quantity: string;
  averageCostPrice?: number | string;
}

export interface ConversionPreview {
  from: ConversionInstrument;
  to: ConversionInstrument;
  holders: ConversionHolders;
  openOrders: ConversionOpenOrders;
  /** First 20 holders only. */
  sample?: ConversionSampleHolder[];
  blockers: ConversionBlocker[];
  warnings: ConversionBlocker[];
  /**
   * Whether the movement will be reported to 1C. Derived from the two ISINs,
   * never a choice: there is no `syncTo1C` request parameter and sending one is
   * rejected. Same-ISIN legs net to zero, so they are not pushed; a genuine
   * ISIN change is.
   */
  syncTo1C?: boolean;
  /**
   * The two ISINs actually compared for that decision — `instrument.isin`,
   * falling back to the ticker half of the code when the row carries none.
   */
  effectiveIsin?: { from: string; to: string };
  /** False whenever `blockers` is non-empty; the execute endpoint enforces the same list. */
  canRun: boolean;
}

export interface ConversionTotals {
  holders: number;
  moved: number;
  skipped: number;
  failed: number;
  /** Decimal string. */
  quantity: string;
}

export interface ConversionCancelledOrders {
  stockSymbols?: string[];
  stockOrderIds?: string[];
  otcOrderIds?: string[];
}

export interface ConversionEntry {
  userId: string;
  /** Decimal string. */
  quantity?: string;
  averageCostPriceBefore?: number | string;
  averageCostPriceAfter?: number | string;
  status: ConversionEntryStatus;
  /** `zero-balance`, `negative-balance`, `frozen:…`, `already-converted`, or an error message. */
  reason?: string;
}

export interface ConversionRun {
  id: string;
  from: string;
  to: string;
  isin?: string;
  status: ConversionStatus;
  adminId?: string;
  note?: string;
  startedAt?: string | number;
  finishedAt?: string | number;
  totals?: ConversionTotals;
  cancelledOrders?: ConversionCancelledOrders;
  /** Pre-flip instrument flags, captured so a revert could be built later. */
  instrumentFlags?: Record<string, unknown>;
  entriesCount?: number;
  /** Present on the detail endpoint only. */
  entries?: ConversionEntry[];
}

export interface ConversionListParams {
  page: number;
  itemsPerPage: number;
  keyword?: string;
  status?: string;
}

export interface ConversionListResponse {
  items: ConversionRun[];
  total: number;
}

@Injectable({ providedIn: 'root' })
export class InstrumentConversionService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  /** Already ends in `/api`, so these resolve to `/api/admin/instrument-conversions`. */
  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  /** Read-only: nothing is persisted, no balance moves, no order is cancelled. */
  preview(payload: ConversionRequest): Observable<ConversionPreview> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/instrument-conversions/preview`,
      payload
    );
  }

  /** Irreversible. Only ever call this from a state the preview authorised. */
  run(payload: RunConversionPayload): Observable<ConversionRun> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/instrument-conversions`,
      payload
    );
  }

  list(params: ConversionListParams): Observable<ConversionListResponse> {
    const query: Record<string, string | number> = {
      page: params.page,
      itemsPerPage: params.itemsPerPage,
    };
    if (params.keyword) query['keyword'] = params.keyword;
    if (params.status) query['status'] = params.status;
    return this.networkHelper.get(
      `${this.hostUrl}/admin/instrument-conversions`,
      { ...query }
    );
  }

  /** Returns the run record with its per-holder entries. */
  getById(id: string): Observable<ConversionRun> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/instrument-conversions/${encodeURIComponent(id)}`
    );
  }
}
